/**
 * Patient Authorization Page
 *
 * Allows patients to view and respond to authorization requests from
 * designated agents who want to manage their healthcare information.
 *
 * This page can be accessed:
 * 1. Directly via /authorization/:authorizationId
 * 2. From the onboarding flow when a user has pending authorizations
 * 3. From the profile page's pending authorizations section
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  PatientAuthorizationForm,
  AuthorizationDetails,
  ConsentType,
} from '../components/authorization';
import {
  ArrowLeft,
  Shield,
  AlertCircle,
  Check,
  RefreshCw,
  LogOut,
} from 'lucide-react';

interface GetPendingAuthorizationsResponse {
  success: boolean;
  authorizations: AuthorizationDetails[];
  totalCount: number;
}

interface ProcessAuthorizationResponse {
  success: boolean;
  message: string;
  action: 'approve' | 'deny';
  caseFileId?: string;
}

interface LocationState {
  fromOnboarding?: boolean;
  returnTo?: string;
}

export function PatientAuthorization(): JSX.Element {
  const { authorizationId } = useParams<{ authorizationId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const { user, signOut } = useAuth();

  const [authorization, setAuthorization] = useState<AuthorizationDetails | null>(null);
  const [allAuthorizations, setAllAuthorizations] = useState<AuthorizationDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ action: 'approve' | 'deny'; message: string } | null>(null);

  // Fetch authorization(s) on mount
  useEffect(() => {
    const fetchAuthorizations = async () => {
      setLoading(true);
      setError(null);

      try {
        const getPendingAuthorizations = httpsCallable<
          { authorizationDocumentId?: string },
          GetPendingAuthorizationsResponse
        >(functions, 'getPendingAuthorizations');

        const result = await getPendingAuthorizations({
          authorizationDocumentId: authorizationId,
        });

        if (result.data.success) {
          setAllAuthorizations(result.data.authorizations);

          if (authorizationId && result.data.authorizations.length > 0) {
            // Show specific authorization
            setAuthorization(result.data.authorizations[0]);
          } else if (result.data.authorizations.length === 1) {
            // Only one pending - show it directly
            setAuthorization(result.data.authorizations[0]);
          } else if (result.data.authorizations.length === 0) {
            setError('No pending authorization requests found for your account.');
          }
          // If multiple, show selection (authorization stays null)
        } else {
          setError('Failed to load authorization requests.');
        }
      } catch (err) {
        console.error('Error fetching authorizations:', err);
        if (err instanceof Error) {
          if (err.message.includes('not-found')) {
            setError('This authorization request was not found or has already been processed.');
          } else if (err.message.includes('permission-denied')) {
            setError('You are not authorized to view this request. Please check that you are signed in with the correct email address.');
          } else {
            setError(err.message || 'Failed to load authorization request.');
          }
        } else {
          setError('Failed to load authorization request. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAuthorizations();
  }, [authorizationId]);

  const handleApprove = async (consentType: ConsentType, consentText: string) => {
    if (!authorization) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const processPatientAuthorization = httpsCallable<
        {
          authorizationDocumentId: string;
          action: 'approve';
          consentType: ConsentType;
          consentText: string;
        },
        ProcessAuthorizationResponse
      >(functions, 'processPatientAuthorization');

      const result = await processPatientAuthorization({
        authorizationDocumentId: authorization.authorizationDocumentId,
        action: 'approve',
        consentType,
        consentText,
      });

      if (result.data.success) {
        setSuccess({
          action: 'approve',
          message: result.data.message,
        });
      } else {
        setError('Failed to process authorization. Please try again.');
      }
    } catch (err) {
      console.error('Error processing authorization:', err);
      if (err instanceof Error) {
        setError(err.message || 'Failed to process authorization.');
      } else {
        setError('Failed to process authorization. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeny = async () => {
    if (!authorization) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const processPatientAuthorization = httpsCallable<
        {
          authorizationDocumentId: string;
          action: 'deny';
        },
        ProcessAuthorizationResponse
      >(functions, 'processPatientAuthorization');

      const result = await processPatientAuthorization({
        authorizationDocumentId: authorization.authorizationDocumentId,
        action: 'deny',
      });

      if (result.data.success) {
        setSuccess({
          action: 'deny',
          message: result.data.message,
        });
      } else {
        setError('Failed to process authorization. Please try again.');
      }
    } catch (err) {
      console.error('Error processing authorization:', err);
      if (err instanceof Error) {
        setError(err.message || 'Failed to process authorization.');
      } else {
        setError('Failed to process authorization. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = () => {
    if (locationState?.returnTo) {
      navigate(locationState.returnTo);
    } else if (locationState?.fromOnboarding) {
      navigate('/onboarding');
    } else {
      navigate('/');
    }
  };

  const handleSelectAuthorization = (auth: AuthorizationDetails) => {
    setAuthorization(auth);
    setError(null);
  };

  const handleBackToList = () => {
    setAuthorization(null);
    setError(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-airy flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-evergreen animate-spin mx-auto mb-4" />
          <p className="text-muted">Loading authorization request...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-airy">
        <header className="bg-white border-b border-soft-neutral">
          <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <h1 className="text-2xl font-display text-deep-forest font-light">Hesvara</h1>
            <span className="text-sm text-muted">{user?.email}</span>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-center">
            <div
              className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                success.action === 'approve'
                  ? 'bg-evergreen/10'
                  : 'bg-amber-100'
              }`}
            >
              {success.action === 'approve' ? (
                <Check className="w-10 h-10 text-evergreen" />
              ) : (
                <AlertCircle className="w-10 h-10 text-amber-600" />
              )}
            </div>

            <h2 className="text-2xl font-display text-deep-forest mb-4">
              {success.action === 'approve'
                ? 'Authorization Approved'
                : 'Authorization Denied'}
            </h2>

            <p className="text-muted mb-8 max-w-md mx-auto">
              {success.message}
            </p>

            {allAuthorizations.length > 1 && (
              <p className="text-sm text-muted mb-4">
                You have {allAuthorizations.length - 1} more pending authorization
                {allAuthorizations.length - 1 > 1 ? 's' : ''} to review.
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {allAuthorizations.length > 1 ? (
                <>
                  <button
                    onClick={() => {
                      // Remove the processed one and show the list
                      setAllAuthorizations((prev) =>
                        prev.filter(
                          (a) =>
                            a.authorizationDocumentId !==
                            authorization?.authorizationDocumentId
                        )
                      );
                      setAuthorization(null);
                      setSuccess(null);
                    }}
                    className="btn-primary"
                  >
                    Review Next Authorization
                  </button>
                  <button onClick={handleContinue} className="btn-secondary">
                    Continue to Dashboard
                  </button>
                </>
              ) : (
                <button onClick={handleContinue} className="btn-primary">
                  Continue
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Error state (no authorizations or permission error)
  if (error && !authorization && allAuthorizations.length === 0) {
    return (
      <div className="min-h-screen bg-airy">
        <header className="bg-white border-b border-soft-neutral">
          <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <h1 className="text-2xl font-display text-deep-forest font-light">Hesvara</h1>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted hidden sm:inline">{user?.email}</span>
              <button
                onClick={signOut}
                className="flex items-center gap-2 text-sm text-muted hover:text-ink transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>

            <h2 className="text-xl font-semibold text-ink mb-4">
              {error.includes('not found') || error.includes('No pending')
                ? 'No Pending Authorizations'
                : 'Unable to Load Authorization'}
            </h2>

            <p className="text-muted mb-8 max-w-md mx-auto">{error}</p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleContinue} className="btn-primary">
                {locationState?.fromOnboarding ? 'Back to Onboarding' : 'Go to Dashboard'}
              </button>
              {error.includes('signed in with the correct email') && (
                <button onClick={signOut} className="btn-secondary">
                  Sign in with Different Account
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Multiple authorizations - show selection list
  if (!authorization && allAuthorizations.length > 1) {
    return (
      <div className="min-h-screen bg-airy">
        <header className="bg-white border-b border-soft-neutral">
          <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <h1 className="text-2xl font-display text-deep-forest font-light">Hesvara</h1>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted hidden sm:inline">{user?.email}</span>
              <button
                onClick={signOut}
                className="flex items-center gap-2 text-sm text-muted hover:text-ink transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-evergreen" />
            </div>
            <h2 className="text-2xl font-display text-deep-forest mb-2">
              Pending Authorization Requests
            </h2>
            <p className="text-muted">
              You have {allAuthorizations.length} authorization request
              {allAuthorizations.length > 1 ? 's' : ''} waiting for your response
            </p>
          </div>

          <div className="space-y-4">
            {allAuthorizations.map((auth) => (
              <div
                key={auth.authorizationDocumentId}
                className="card cursor-pointer hover:border-evergreen hover:shadow-md transition-all"
                onClick={() => handleSelectAuthorization(auth)}
              >
                <div className="card-body">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-ink">{auth.caseFileName}</h3>
                      <p className="text-sm text-muted mt-1">
                        Requested by: {auth.requestedBy.name} ({auth.requestedBy.relationship})
                      </p>
                      <p className="text-sm text-muted">
                        Received: {new Date(auth.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <button className="btn-secondary btn-sm">Review</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <button onClick={handleContinue} className="btn-ghost text-muted">
              Skip for now
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Single authorization - show the form
  return (
    <div className="min-h-screen bg-airy">
      <header className="bg-white border-b border-soft-neutral">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <h1 className="text-2xl font-display text-deep-forest font-light">Hesvara</h1>
          <div className="flex items-center gap-4">
            {allAuthorizations.length > 1 && (
              <button
                onClick={handleBackToList}
                className="btn-ghost btn-sm flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to List
              </button>
            )}
            <span className="text-sm text-muted hidden sm:inline">{user?.email}</span>
            <button
              onClick={signOut}
              className="flex items-center gap-2 text-sm text-muted hover:text-ink transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {error && (
          <div className="alert alert-error mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {authorization && (
          <PatientAuthorizationForm
            authorization={authorization}
            onApprove={handleApprove}
            onDeny={handleDeny}
            isSubmitting={isSubmitting}
          />
        )}
      </main>
    </div>
  );
}

export default PatientAuthorization;
